import { CatalogItemKind } from 'fym-shared/src/smartlinks/catalog';

/** The two kinds of page the sheet can build out of a release. */
export enum CreationPageType {
  release = 'release',
  presave = 'presave',
}

/** Why the sheet reached its conclusion, so the box can say it in one line. */
export enum CreationPageReason {
  /** The row is one the artist added by hand and has not put out. */
  unreleasedRow = 'unreleasedRow',
  /** The release date is still ahead. */
  futureDate = 'futureDate',
  /** The release date has passed. */
  pastDate = 'pastDate',
  /** No date, but the links exist, so it is out somewhere. */
  alreadyLinked = 'alreadyLinked',
  /** Nothing to route to at all. */
  nothingToRouteTo = 'nothingToRouteTo',
}

export interface PageTypeInference {
  type: CreationPageType;
  reason: CreationPageReason;
}

export interface PageTypeInferenceInput {
  /** The catalog row the sheet started from, when it started from one. */
  catalogKind?: CatalogItemKind;
  /** `YYYY-MM-DD`, when the source knows the day. */
  releaseDate?: string;
  /** Whether at least one music service link resolved. */
  isResolved: boolean;
  /** Today in UTC, `YYYY-MM-DD` — the clock release dates are stored on. */
  today: string;
}

/**
 * What kind of page this is, read off the release rather than asked for.
 *
 * The order matters. An unreleased row is the artist's own word that the track
 * is not out, and it beats every other signal — including a date that has
 * slipped. A date settles the rest. With no date at all we fall back on
 * whether anything answered: links mean it is out somewhere, and nothing at
 * all means there is nothing to route to yet.
 */
export function inferPageType({
  catalogKind,
  releaseDate,
  isResolved,
  today,
}: PageTypeInferenceInput): PageTypeInference {
  if (catalogKind === CatalogItemKind.unreleased) {
    return { type: CreationPageType.presave, reason: CreationPageReason.unreleasedRow };
  }
  if (releaseDate !== undefined) {
    return releaseDate > today
      ? { type: CreationPageType.presave, reason: CreationPageReason.futureDate }
      : { type: CreationPageType.release, reason: CreationPageReason.pastDate };
  }
  if (isResolved) {
    return { type: CreationPageType.release, reason: CreationPageReason.alreadyLinked };
  }
  return { type: CreationPageType.presave, reason: CreationPageReason.nothingToRouteTo };
}

/** Today in UTC, the clock the release dates are stored on. */
export function todayUTC(now: number): string {
  return new Date(now).toISOString().slice(0, 10);
}
